import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BackendTables, STAGE, UserGroup } from "../../../interfaces/db"
import { BackendFilter, FilterOperator } from "../../../interfaces/sessions"
import { CallMetricColumns, PROSPECT_INFO_COLUMNS, SESSION_METRICS_COLUMNS } from "../../../interfaces/table"
import { MetricSelectV6 } from "interfaces/metrics";
import { LIVE_DURATION_GT_ZERO } from "../../../cfg/analytics";

export type StageDefinition = {
    label: string
    filter: BackendFilter
}

export type StageConfig = {
    options: StageDefinition[]
    cumulative?: boolean // If true: each stage includes CNFs from previous stages (except last)
}

export function stagesToFilter(stages: STAGE[]): BackendFilter {
  return [[{
    column_name: SESSION_METRICS_COLUMNS.STAGE,
    table: BackendTables.SESSION_METRICS,
    operator: FilterOperator.IN,
    value_safe: stages
  }]]
}

export enum STANDARD_STAGES {
  DIAL = 'Dial',
  CONNECT = 'Connect',
  PITCH = 'Pitch',
  CONVERSATION = 'Conversation',
  MEETING = 'Meeting',
}

export const DIAL_DEFINITION: StageDefinition = {
  label: STANDARD_STAGES.DIAL,
  filter: []
}

export const CONNECT_DEFINITION: StageDefinition = {
  label: STANDARD_STAGES.CONNECT,
  filter: [[LIVE_DURATION_GT_ZERO]]
}

export const PITCH_DEFINITION: StageDefinition = {
  label: STANDARD_STAGES.PITCH,
  filter: [[{
    column_name: SESSION_METRICS_COLUMNS.STAGE,
    table: BackendTables.SESSION_METRICS,
    operator: FilterOperator.IN,
    value_safe: [STAGE.PITCHED, STAGE.CONVERSATION, STAGE.BOOKED]
  }, {
    column_name: SESSION_METRICS_COLUMNS.LIVE_DURATION,
    table: BackendTables.SESSION_METRICS,
    operator: FilterOperator.GT,
    value_safe: 60
  }]]
}

export const CONVERSATION_DEFINITION: StageDefinition = {
  label: STANDARD_STAGES.CONVERSATION,
  filter: [[{
    column_name: SESSION_METRICS_COLUMNS.STAGE,
    table: BackendTables.SESSION_METRICS,
    operator: FilterOperator.IN,
    value_safe: [STAGE.CONVERSATION, STAGE.BOOKED]
  }, {
    column_name: SESSION_METRICS_COLUMNS.LIVE_DURATION,
    table: BackendTables.SESSION_METRICS,
    operator: FilterOperator.GT,
    value_safe: 90
  }]]
}

export const MEETING_DEFINITION: StageDefinition = {
  label: STANDARD_STAGES.MEETING,
  filter: [[{
    column_name: SESSION_METRICS_COLUMNS.STAGE,
    table: BackendTables.SESSION_METRICS,
    operator: FilterOperator.IN,
    value_safe: [STAGE.BOOKED]
  }]]
}

export const DEFAULT_STAGE_CONFIG: StageConfig = {
    options: [
      DIAL_DEFINITION,
      CONNECT_DEFINITION,
      PITCH_DEFINITION,
      CONVERSATION_DEFINITION,
      MEETING_DEFINITION,
    ],
    cumulative: true, // Default true for standard stage config
}

export type StageConfigResult = {
  stage_config: StageConfig | null
  saved_stage_config: StageConfig | null
  editing: boolean
}

const stageConfigInitialState: StageConfigResult = {
  stage_config: null,
  saved_stage_config: null,
  editing: false,
}

export const stageConfigSlice = createSlice({
  name: 'stageConfig',
  initialState: stageConfigInitialState,
  reducers: {
    loadStageConfigs: (state, action: PayloadAction<StageConfig | null>) => { 
      const config = action.payload || DEFAULT_STAGE_CONFIG;
      state.stage_config = config;
      state.saved_stage_config = config;
    },
    updateSavedStageConfig: (state, action: PayloadAction<StageConfig>) => { 
      state.saved_stage_config = action.payload;
    },
    updateStageConfig: (state, action: PayloadAction<StageConfig>) => { 
      state.stage_config = action.payload;
    },
    setEditing: (state, action: PayloadAction<boolean>) => { 
      state.editing = action.payload;
    },
  }
})

export function getMetricForStage(stage: StageDefinition, stageConfig?: StageConfig): MetricSelectV6 {
  // If stageConfig is provided and cumulative is true, include CNFs from previous stages (except for the last stage)
  let combinedFilter = [...stage.filter]

  if (stageConfig && stageConfig.cumulative === true) {
    const allStages = stageConfig.options
    const stageIndex = allStages.findIndex(s => s === stage)
    const isLastStage = stageIndex === allStages.length - 1

    if (!isLastStage) {
      // For all stages except the last, include filters from all previous stages
      for (let i = 0; i < stageIndex; i++) {
        const previousStage = allStages[i]
        if (previousStage.filter && previousStage.filter.length > 0) {
          combinedFilter.push(...previousStage.filter)
        }
      }
    }
  }

  return {
    selects: [{
      column_type: "CNF",
      cnf: combinedFilter
    }],
    cnf: [],
    group_by: null
  }
}

export const { loadStageConfigs, updateSavedStageConfig, updateStageConfig, setEditing } = stageConfigSlice.actions; 